/**
 * Klient för admin-only kontaktuppgifter (S-04-31 / backend-S-02-12).
 *
 * Wrappar:
 *   - GET  /api/admin/users/{userId}/contacts — hämta
 *   - PUT  /api/admin/users/{userId}/contacts — skapa/uppdatera
 *
 * Hard-rules från CLAUDE.md:
 *  - Cookie-baserad auth via `credentials: 'include'`. Inga tokens i
 *    body eller localStorage/sessionStorage.
 *  - CSRF-header på muterande anrop (PUT). GET är inte CSRF-skyddad.
 *  - Inga råa databas-ID:n exponeras — endpointerna tar emot UUID och
 *    returnerar UUID + kontaktuppgifter.
 *  - Backend-felkoder är opaka tokens; UI mappar till svenska användar-
 *    texter via [messageForError].
 *
 * Datakategori: "Kontaktuppgifter (admin-only)" — namn, mobil, e-post.
 * Mobilappen har INGEN åtkomst till denna data.
 */

import { csrfHeader } from '../auth/csrf';

export type UserContacts = {
  userId: string;
  /** Obligatoriskt namn. */
  name: string;
  /** E.164-format `+landskod + nummer` (8–15 siffror). Null om ej angivet. */
  phone: string | null;
  /** E-postadress. Null om ej angivet. */
  email: string | null;
  /** ISO-8601 timestamp för senaste uppdateringen. */
  updatedAt: string;
};

export type GetUserContactsResult =
  | { kind: 'ok'; contacts: UserContacts }
  | { kind: 'not_found' }
  | { kind: 'unauthorized' }
  | { kind: 'failure'; message: string };

export type UpdateUserContactsResult =
  | { kind: 'ok'; contacts: UserContacts }
  | { kind: 'invalid'; field: 'name' | 'phone' | 'email'; message: string }
  | { kind: 'not_found' }
  | { kind: 'unauthorized' }
  | { kind: 'failure'; message: string };

/**
 * Validering speglar backend-CHECK + service-validering. Görs lokalt
 * innan submit för att ge omedelbar feedback. Backend re-validerar
 * alltid — detta är bara UX, ingen säkerhetsgräns.
 */
const PHONE_REGEX = /^\+[0-9]{8,15}$/;
const EMAIL_REGEX = /^[^@]+@[^@]+\.[^@]+$/;

export const NAME_MAX_LENGTH = 200;
export const EMAIL_MAX_LENGTH = 255;

export type ValidationError = {
  field: 'name' | 'phone' | 'email';
  message: string;
};

/**
 * Validerar input enligt samma regler som backend:
 *  - name: krävs, 1..200 tecken efter trim
 *  - phone: valfri, format `+landskod + nummer`
 *  - email: valfri, minst ett @ och ett .
 *
 * Returnerar första felet eller null om allt är ok.
 */
export function validateUserContacts(input: {
  name: string;
  phone: string;
  email: string;
}): ValidationError | null {
  const name = input.name.trim();
  if (name.length === 0) {
    return { field: 'name', message: 'Namn krävs.' };
  }
  if (name.length > NAME_MAX_LENGTH) {
    return { field: 'name', message: `Namn får vara högst ${NAME_MAX_LENGTH} tecken.` };
  }

  const phone = input.phone.trim();
  if (phone.length > 0 && !PHONE_REGEX.test(phone)) {
    return {
      field: 'phone',
      message: 'Mobilnummer måste vara på formatet +landskod + nummer (t.ex. +46701234567).',
    };
  }

  const email = input.email.trim();
  if (email.length > 0) {
    if (!EMAIL_REGEX.test(email)) {
      return { field: 'email', message: 'Ogiltig e-postadress.' };
    }
    if (email.length > EMAIL_MAX_LENGTH) {
      return { field: 'email', message: `E-post får vara högst ${EMAIL_MAX_LENGTH} tecken.` };
    }
  }
  return null;
}

function basePath(userId: string): string {
  return `/api/admin/users/${encodeURIComponent(userId)}/contacts`;
}

function messageForError(code: string): string {
  switch (code) {
    case 'unauthorized':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'forbidden':
      return 'Saknar behörighet att hantera kontaktuppgifter.';
    case 'not_found':
      return 'Inga kontaktuppgifter hittades.';
    case 'invalid_user_id':
      return 'Ogiltigt användar-id.';
    case 'name_required':
      return 'Namn krävs.';
    case 'name_too_long':
      return `Namn får vara högst ${NAME_MAX_LENGTH} tecken.`;
    case 'phone_format':
      return 'Mobilnummer måste vara på formatet +landskod + nummer.';
    case 'email_format':
      return 'Ogiltig e-postadress.';
    case 'email_too_long':
      return `E-post får vara högst ${EMAIL_MAX_LENGTH} tecken.`;
    case 'network':
      return 'Kunde inte nå servern. Kontrollera anslutningen.';
    default:
      return 'Något gick fel. Försök igen om en stund.';
  }
}

async function safeJson(response: Response): Promise<{ error?: string } | null> {
  try {
    return (await response.json()) as { error?: string };
  } catch {
    return null;
  }
}

type RawContacts = {
  user_id?: unknown;
  name?: unknown;
  phone?: unknown;
  email?: unknown;
  updated_at?: unknown;
};

function parseContacts(raw: RawContacts): UserContacts | null {
  if (
    typeof raw.user_id !== 'string' ||
    typeof raw.name !== 'string' ||
    typeof raw.updated_at !== 'string'
  ) {
    return null;
  }
  const phone = raw.phone === null || raw.phone === undefined ? null : String(raw.phone);
  const email = raw.email === null || raw.email === undefined ? null : String(raw.email);
  return {
    userId: raw.user_id,
    name: raw.name,
    phone,
    email,
    updatedAt: raw.updated_at,
  };
}

export async function getUserContacts(userId: string): Promise<GetUserContactsResult> {
  let response: Response;
  try {
    response = await fetch(basePath(userId), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.status === 404) {
    return { kind: 'not_found' };
  }
  if (!response.ok) {
    const body = await safeJson(response);
    return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }
  const parsed = parseContacts(raw as RawContacts);
  if (!parsed) {
    return { kind: 'failure', message: messageForError('default') };
  }
  return { kind: 'ok', contacts: parsed };
}

export async function updateUserContacts(
  userId: string,
  body: { name: string; phone: string; email: string },
): Promise<UpdateUserContactsResult> {
  // Pre-validering klient-side — ger omedelbar feedback. Backend
  // re-validerar alltid.
  const validation = validateUserContacts(body);
  if (validation) {
    return { kind: 'invalid', field: validation.field, message: validation.message };
  }

  let response: Response;
  try {
    response = await fetch(basePath(userId), {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body: JSON.stringify({
        name: body.name.trim(),
        // Tom sträng tolkas som null på backend ("rensa fältet")
        phone: body.phone.trim().length === 0 ? null : body.phone.trim(),
        email: body.email.trim().length === 0 ? null : body.email.trim(),
      }),
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.status === 404) {
    return { kind: 'not_found' };
  }
  if (response.status === 400) {
    const errBody = await safeJson(response);
    const code = errBody?.error ?? 'default';
    if (code === 'name_required' || code === 'name_too_long') {
      return { kind: 'invalid', field: 'name', message: messageForError(code) };
    }
    if (code === 'phone_format') {
      return { kind: 'invalid', field: 'phone', message: messageForError(code) };
    }
    if (code === 'email_format' || code === 'email_too_long') {
      return { kind: 'invalid', field: 'email', message: messageForError(code) };
    }
    return { kind: 'failure', message: messageForError(code) };
  }
  if (!response.ok) {
    const errBody = await safeJson(response);
    return { kind: 'failure', message: messageForError(errBody?.error ?? 'default') };
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }
  const parsed = parseContacts(raw as RawContacts);
  if (!parsed) {
    return { kind: 'failure', message: messageForError('default') };
  }
  return { kind: 'ok', contacts: parsed };
}
